import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Grid, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BiotechIcon from '@mui/icons-material/Biotech';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SEO from '../components/SEO';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  height: '100%',
  background: 'linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)',
}));

const aboutStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Thrana',
  url: 'https://www.thranatech.com/about',
  description:
    'Thrana pioneers pharmacovigilance platforms and services, combining pharmaceutical expertise with advanced technology.',
  publisher: {
    '@type': 'Organization',
    name: 'Thranatech.com',
    url: 'https://www.thranatech.com',
  },
};

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const About = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/banner2.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(timer);
  }, []);
  return (
    <>
    
      <SEO
        title="About Thrana | Pharmacovigilance Leadership"
        description="Learn how Thrana combines pharmaceutical expertise with innovative technology to deliver world-class pharmacovigilance software, services, and training."
        path="/about"
        structuredData={aboutStructuredData}
      />
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              About Us
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                color: 'white',
              }}
            >
              Leading Pharmacovigilance Solutions
            </Typography>
          </Container>
        </Overlay>
      </HeroSection>

      <Box sx={{ py: 8 }}>
        <Container>

        <Box sx={{ my: 6 }}>
          <Typography variant="body1" paragraph>
            At Thrana, we are at the forefront of revolutionizing pharmacovigilance through innovative technology. 
            Our mission is to transform drug safety monitoring by leveraging advanced technologies to make the process 
            more efficient, accurate, and proactive.
          </Typography>
          <Typography variant="body1" paragraph>
            Founded by a team of technology experts and pharmaceutical industry veterans, we combine deep domain knowledge with 
            advanced capabilities to deliver solutions that set new standards in pharmacovigilance.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6}>
            <StyledPaper elevation={3}>
              <PsychologyIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                Our Vision
              </Typography>
              <Typography>
                To be the global leader in pharmacovigilance solutions, making drug safety monitoring more 
                efficient and reliable for pharmaceutical companies worldwide.
              </Typography>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledPaper elevation={3}>
              <BiotechIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                Our Expertise
              </Typography>
              <Typography>
                Specializing in pharmacovigilance operations, data management, and regulatory compliance, 
                we help companies identify and analyze adverse events efficiently and accurately.
              </Typography>
            </StyledPaper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 8 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ color: '#3c005a' }}>
            Why Choose Us
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <StyledPaper elevation={3}>
                <GroupsIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Expert Team
                </Typography>
                <Typography>
                  Our team combines AI specialists, pharmacovigilance experts, and regulatory professionals
                </Typography>
              </StyledPaper>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledPaper elevation={3}>
                <EmojiEventsIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Proven Track Record
                </Typography>
                <Typography>
                  Successfully implemented AI solutions for leading pharmaceutical companies worldwide
                </Typography>
              </StyledPaper>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledPaper elevation={3}>
                <PsychologyIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Innovation
                </Typography>
                <Typography>
                  Continuously developing new AI technologies to advance pharmacovigilance practices
                </Typography>
              </StyledPaper>
            </Grid>
          </Grid>
        </Box>
        </Container>
      </Box>
    </>
  );
};

export default About; 